import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'sonner';
import { useEffect, useLayoutEffect, useState, lazy, Suspense } from 'react';
import { CartProvider } from './context/CartContext.jsx';
import { AuthProvider, useAuth } from './context/AuthContext.jsx';
import { LanguageProvider } from './context/LanguageContext.jsx';
import Header from './components/Header';
import Footer from './components/Footer';
import CartSidebar from './components/CartSidebar';
import AgeVerification from './components/AgeVerification';
import VerifyEmailBanner from './components/VerifyEmailBanner';
import SpanishWelcomeBanner from './components/SpanishWelcomeBanner';
import LegacyRedirector from './components/LegacyRedirector';
import PromotionAnnouncementBar from './components/PromotionAnnouncementBar';
import { ClarityTracker, AnalyticsConsentBanner } from './components/AnalyticsConsentBanner';
import Home from './pages/Home';
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const CoAVerify = lazy(() => import('./pages/CoAVerify'));

// Lazy-loaded routes — split into separate chunks for better FCP/LCP
const Products = lazy(() => import('./pages/Products'));
const About = lazy(() => import('./pages/About'));
const Quality = lazy(() => import('./pages/Quality'));
const Contact = lazy(() => import('./pages/Contact'));
const Disclaimer = lazy(() => import('./pages/Disclaimer'));
const Terms = lazy(() => import('./pages/Terms'));
const Privacy = lazy(() => import('./pages/Privacy'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));
const TrackOrder = lazy(() => import('./pages/TrackOrder'));
const Reviews = lazy(() => import('./pages/Reviews'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const HowToOrder = lazy(() => import('./pages/HowToOrder'));
// NOTE (2026-02): Public reconstitution/dose-preparation calculator removed
// from the site. Its primary purpose was reconstitution + syringe-unit
// dose preparation, which is out of scope for a research-supplier catalog.
// Both /calculator and /peptide-calculator now fall through to NotFound (404).
// Do not re-add these routes without an explicit product decision.
const Admin = lazy(() => import('./pages/Admin'));
const InvoiceConfirmation = lazy(() => import('./pages/InvoiceConfirmation'));
const Checkout = lazy(() => import('./pages/Checkout'));
const OrderHistory = lazy(() => import('./pages/OrderHistory'));
const Wishlist = lazy(() => import('./pages/Wishlist'));
const ChatDashboard = lazy(() => import('./pages/ChatDashboard'));
const ChatRoom = lazy(() => import('./pages/ChatRoom'));
const NotificationSettings = lazy(() => import('./pages/NotificationSettings'));
const JoinChat = lazy(() => import('./pages/JoinChat'));
const CommunityGuidelines = lazy(() => import('./pages/CommunityGuidelines'));
const ReferralProgram = lazy(() => import('./pages/ReferralProgram'));
const TirzepatideResearch = lazy(() => import('./pages/TirzepatideResearch'));
const GLP1Research = lazy(() => import('./pages/GLP1Research'));
const SemaglutideResearch = lazy(() => import('./pages/SemaglutideResearch'));
// PeptideCalculator route removed (2026-02) — see NotFound fall-through above.
const ResearchPeptideGuide = lazy(() => import('./pages/ResearchPeptideGuide'));
const Unsubscribe = lazy(() => import('./pages/Unsubscribe'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogArticle = lazy(() => import('./pages/BlogArticle'));
const CategoryPage = lazy(() => import('./pages/CategoryPage'));
const InvoiceLookup = lazy(() => import('./pages/InvoiceLookup'));
const PartnerProgram = lazy(() => import('./pages/PartnerProgram'));
const PartnerDashboard = lazy(() => import('./pages/PartnerDashboard'));
const NotFound = lazy(() => import('./pages/NotFound'));
const CoALibrary = lazy(() => import('./pages/CoALibrary'));
const Promotions = lazy(() => import('./pages/Promotions'));
import { TawkVisibility } from './components/TawkVisibility';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Get or create a persistent visitor ID
function getVisitorId() {
  let vid = localStorage.getItem('rcm_visitor_id');
  if (!vid) {
    vid = 'v_' + Math.random().toString(36).substr(2, 12) + Date.now().toString(36);
    localStorage.setItem('rcm_visitor_id', vid);
  }
  return vid;
}

// Analytics tracker component
function AnalyticsTracker() {
  const location = useLocation();
  const { user } = useAuth();

  // Capture partner ?ref=PT-XXXX once per session
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = (params.get('ref') || '').toUpperCase().trim();
      if (ref && ref.startsWith('PT-')) {
        const stored = localStorage.getItem('rcm_partner_code');
        if (stored !== ref) {
          localStorage.setItem('rcm_partner_code', ref);
          localStorage.setItem('rcm_partner_captured_at', String(Date.now()));
          fetch(`${API_URL}/api/partners/click`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              code: ref,
              visitor_id: getVisitorId(),
              referrer: document.referrer,
              user_agent: navigator.userAgent,
            }),
          }).catch(() => {});
        }
      }
    } catch {}
  }, []);

  useEffect(() => {
    // Skip tracking for admin paths
    if (location.pathname.startsWith('/admin')) {
      localStorage.setItem('rcm_is_admin', 'true');
      return;
    }
    // Skip if admin flag set OR admin session active
    if (localStorage.getItem('rcm_is_admin') === 'true') return;
    if (sessionStorage.getItem('rcm_admin_auth') === 'true') {
      localStorage.setItem('rcm_is_admin', 'true');
      return;
    }
    // Skip excluded email
    if (user?.email === 'ronalmapps42@gmail.com') {
      localStorage.setItem('rcm_is_admin', 'true');
      return;
    }

    fetch(`${API_URL}/api/analytics/pageview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        page: location.pathname,
        referrer: document.referrer,
        user_agent: navigator.userAgent,
        visitor_id: getVisitorId(),
        user_id: user?.id || null,
        user_name: user?.name || null,
        is_admin: false
      })
    }).catch(() => {});
  }, [location.pathname, user]);

  return null;
}

// Lightweight loading fallback for lazy routes
const RouteFallback = () => (
  <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <div style={{ width: 32, height: 32, border: '3px solid rgba(0,229,255,0.2)', borderTopColor: '#00E5FF', borderRadius: '50%', animation: 'rcm-spin 0.8s linear infinite' }} />
    <style>{`@keyframes rcm-spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

function SsrShellRelease() {
  useLayoutEffect(() => { window.__releaseSsrShell?.(); }, []);
  return null;
}

function AppLayout() {
  const location = useLocation();
  const isChatPage = location.pathname.startsWith('/chat/') || location.pathname === '/messages';
  const storefront = !/^\/admin(\/|$)/.test(location.pathname);

  // Scroll reveal observer
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, { threshold: 0, rootMargin: '200px 0px 200px 0px' });

    const timer = setTimeout(() => {
      document.querySelectorAll('.reveal').forEach(el => observer.observe(el));
    }, 100);

    return () => { clearTimeout(timer); observer.disconnect(); };
  }, [location.pathname]);
  
  return (
    <>
      <LegacyRedirector />
      <AnalyticsTracker />
      <ClarityTracker />
      <AnalyticsConsentBanner />
      {storefront && <PromotionAnnouncementBar />}
      {storefront && <VerifyEmailBanner />}
      {storefront && <SpanishWelcomeBanner />}
      {storefront && !isChatPage && <Header />}
      {storefront && <CartSidebar />}
      <main>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/products" element={<Products />} />
            <Route path="/about" element={<About />} />
            <Route path="/quality" element={<Quality />} />
            <Route path="/coa" element={<CoALibrary />} />
            <Route path="/coa/:batch" element={<CoAVerify />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/privacy-policy" element={<Privacy />} />
            <Route path="/track-order" element={<TrackOrder />} />
            <Route path="/invoice-lookup" element={<InvoiceLookup />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/reviews" element={<Reviews />} />
            <Route path="/product/:catNo" element={<ProductDetail />} />
            <Route path="/products/:slug" element={<ProductDetail />} />
            <Route path="/how-to-order" element={<HowToOrder />} />
            {/* /calculator and /peptide-calculator routes removed (2026-02).
                Any request falls through to <NotFound /> at the catch-all. */}
            <Route path="/confirm/:token" element={<InvoiceConfirmation />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/order-history" element={<OrderHistory />} />
            <Route path="/wishlist" element={<Wishlist />} />
            {/* Community/chat routes — disabled, set REACT_APP_COMMUNITY_ENABLED=true to restore */}
            {process.env.REACT_APP_COMMUNITY_ENABLED === 'true' && (
              <>
                <Route path="/community" element={<ChatDashboard />} />
                <Route path="/community/guidelines" element={<CommunityGuidelines />} />
                <Route path="/chat/:roomId" element={<ChatRoom />} />
                <Route path="/chat/join/:token" element={<JoinChat />} />
                <Route path="/messages" element={<ChatDashboard />} />
              </>
            )}
            <Route path="/settings/notifications" element={<NotificationSettings />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/admin/customers" element={<Admin />} />
            <Route path="/admin/customers/:id" element={<Admin />} />
            <Route path="/referral-program" element={<ReferralProgram />} />
            <Route path="/referral" element={<ReferralProgram />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/partner-program" element={<PartnerProgram />} />
            <Route path="/partner-dashboard" element={<PartnerDashboard />} />
            <Route path="/tirzepatide-research" element={<TirzepatideResearch />} />
            <Route path="/glp1-research" element={<GLP1Research />} />
            <Route path="/semaglutide-research" element={<SemaglutideResearch />} />
            <Route path="/research-peptide-guide" element={<ResearchPeptideGuide />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/promotions" element={<Promotions />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogArticle />} />
            {/* Category pages */}
            <Route path="/weight-management" element={<CategoryPage />} />
            <Route path="/tissue-recovery" element={<CategoryPage />} />
            <Route path="/cognitive-neuro" element={<CategoryPage />} />
            <Route path="/longevity" element={<CategoryPage />} />
            <Route path="/growth-hormone" element={<CategoryPage />} />
            <Route path="/immune" element={<CategoryPage />} />
            <Route path="/blends" element={<CategoryPage />} />
            <Route path="/lab-supplies" element={<CategoryPage />} />
            {/* Catch-all — any unmatched URL renders the dedicated 404 page.
                MUST come last. Previously a `/:categorySlug` catch-all sent
                unknown 1-segment paths to CategoryPage (which then displayed
                a "Category Not Found" state at HTTP 200); that behavior meant
                stale links, misspellings, and legacy URLs silently rendered
                pseudo-homepage content to crawlers. Explicit NotFound instead. */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          <SsrShellRelease />
        </Suspense>
      </main>
      {storefront && !isChatPage && <Footer />}
      <TawkVisibility />
      <Toaster position="top-right" />
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <LanguageProvider>
          <AuthProvider>
            <CartProvider>
              <AgeVerification>
                <div className="App">
                  <AppLayout />
                </div>
              </AgeVerification>
            </CartProvider>
          </AuthProvider>
        </LanguageProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
